import { useEffect, useState } from 'react'

import { useRouter } from 'next/router'

import { ReactSVG } from 'react-svg'

import { IFormattedLoan } from '../../../../../../store/fetchers/loans/enum'

import { Tabs } from '../../../../../../components/ui/tabs'

import { EZones } from '../../../../components/status-list'

import { LoanStatus } from '../../../../loans/utils'

import styles from './styles.module.scss'
import { LoansHealthProps } from './loans-health.props'
import ArrowRight from './arrow-right.svg'
import { useTranslation } from 'utils/use-translation'

export const TabNames = {
  GREEN: 'Green',
  ORANGE: 'Orange',
  RED: 'Red',
}

type TabName = typeof TabNames[keyof typeof TabNames]

export default function LoansHealth({ loans }: LoansHealthProps) {
  const { t } = useTranslation()

  const [activeTab, setActiveTab] = useState<TabName>(TabNames.GREEN)
  const router = useRouter()
  const [filteredLoans, setFilteredLoans] = useState<IFormattedLoan[]>([])
  const [countOfSelectedLoans, setCountOfSelectedLoans] = useState<number>()
  const [zoneText, setZoneText] = useState<string>(`👍 ${t('green_zone_definition')}`)
  const tabs = [
    { name: TabNames.GREEN, count: filteredLoans?.filter((item) => item.current_zone === 3)?.length },
    {
      name: TabNames.ORANGE,
      count: filteredLoans?.filter((item) => item.current_zone === 2 || item.current_zone === 1)?.length,
    },
    {
      name: TabNames.RED,
      count: filteredLoans?.filter((item) => item.current_zone <= 0)?.length,
    },
  ]

  useEffect(() => {
    if (loans.length) {
      setFilteredLoans(
        loans.filter(
          (item) => item.status === 'order_created' || item.status === 'loan_failed' || item.status === 'loan_success',
        ),
      )
    }
  }, [loans])

  useEffect(() => {
    if (activeTab) {
      switch (activeTab) {
        case TabNames.GREEN:
          setCountOfSelectedLoans(filteredLoans.filter((item) => item.current_zone === 3).length)
          setZoneText(`👍 ${t('green_zone_definition')}`)
          break
        case TabNames.ORANGE:
          setCountOfSelectedLoans(
            filteredLoans.filter((item) => item.current_zone === 2 || item.current_zone === 1).length,
          )
          setZoneText(
            `🤞 ${t('orange_zone_definition')}`,
          )
          break
        case TabNames.RED:
          setCountOfSelectedLoans(filteredLoans.filter((item) => item.current_zone <= 0).length)
          setZoneText(
            `📛 ${t('red_zone_definition')}`,
          )
          break
        default:
          break
      }
    }
  }, [filteredLoans, activeTab])

  const checkLoans = () => {
    if (activeTab?.toLowerCase() === 'green') {
      localStorage.setItem('loans::zonesFilter', JSON.stringify([EZones.GREEN]))
      localStorage.setItem('loans::statusFilter', JSON.stringify([LoanStatus.Active]))
    } else if (activeTab?.toLowerCase() === 'orange') {
      localStorage.setItem('loans::zonesFilter', JSON.stringify([EZones.ORANGE]))
      localStorage.setItem('loans::statusFilter', JSON.stringify([LoanStatus.Active]))
    } else if (activeTab?.toLowerCase() === 'red') {
      localStorage.setItem('loans::zonesFilter', JSON.stringify([EZones.RED]))
      localStorage.setItem('loans::statusFilter', JSON.stringify([LoanStatus.Active]))
    }
    router.push('/account/loans')
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab)
  }

  return (
    <div className={styles['loans-health']}>
      <h3>{t('loans_health')}</h3>
      <p className={styles.text_mobile}>{t('you_have')} {filteredLoans?.length} {t('loans')}</p>
      <div className={styles.tabs}>
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          isZones={true}
          loansCount={filteredLoans?.length}
        />
      </div>

      {activeTab === TabNames.GREEN && (
        <div className={styles.content}>
          <h5>
            <span className={styles.green_count}>
              {countOfSelectedLoans}/{filteredLoans?.length}
            </span>{' '}
            {t('of_your_loans_are_in')} <span className={styles.green_zone}>{t('green')}</span> {t('zone_info')}</h5>

          <div className={styles.content__main}>
            <p className={styles.text}>{zoneText}</p>
            <div className={styles.checkLoans}>
              <button onClick={checkLoans}>
                <p>{t('check_loans')}</p> <ReactSVG src={ArrowRight} className={styles.icon} />
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === TabNames.ORANGE && (
        <div className={styles.content}>
          <h5>
            <span className={styles.orange_count}>
              {countOfSelectedLoans}/{filteredLoans?.length}
            </span>{' '}
            {t('of_your_loans_are_in')} <span className={styles.orange_zone}>{t('orange_zone')}</span> {t('zone_info')}</h5>

          <div className={styles.content__main}>
            <p className={styles.text}>{zoneText}</p>
            <div className={styles.checkLoans}>
              <button onClick={checkLoans}>
                <p>{t('check_loans')}</p> <ReactSVG src={ArrowRight} className={styles.icon} />
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === TabNames.RED && (
        <div className={styles.content}>
          <h5>
            <span className={styles.red_count}>
              {countOfSelectedLoans}/{filteredLoans?.length}
            </span>{' '}
            {t('of_your_loans_are_in')} <span className={styles.red_zone}>{t('red')}</span> {t('zone_info')}</h5>

          <div className={styles.content__main}>
            <p className={styles.text}>{zoneText}</p>
            <div className={styles.checkLoans}>
              <button onClick={checkLoans}>
                <p>{t('check_loans')}</p> <ReactSVG src={ArrowRight} className={styles.icon} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
